import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ButtonType, Select } from '@nhl/nhl-breakaway-ui';

import { statsConfigSelectors } from '../../../../../../redux/ducks/config';
import { filterActionCreators } from '../../../../../../redux/ducks/playersAndTeams/filter';
import { getCustomFilterMessage } from '../../utils';

import { InputsContainer, ValueInput, AddButton } from './styles';

const MAX_FILTERS = 3;
const OPERATOR_OPTIONS = [
    { key: 'gte', value: 'gte', label: '>=' },
    { key: 'equals', value: 'equals', label: '=' },
    { key: 'lte', value: 'lte', label: '<=' },
];

const Inputs = () => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const statsConfig = useSelector((state) => state.config);
    const reportCategory = useSelector((state) => state.playersAndTeams.report.reportCategory);
    const appliedReportName = useSelector((state) => state.playersAndTeams.report.applied.reportName);
    const timeFrame = useSelector((state) => state.playersAndTeams.report.applied.timeFrame);
    const customFilters = useSelector((state) => state.playersAndTeams.filter.values.customFilters);
    const canAnotherFilterBeAdded = MAX_FILTERS > customFilters.length;

    // Get the measures for the report
    const reportData = statsConfigSelectors.getReportData(statsConfig, appliedReportName);
    const seasonOrGame = timeFrame !== 'game' ? 'season' : 'game';

    const createMeasureOptions = () => {
        return reportData[seasonOrGame].resultFilters.map((measure) => ({
            key: measure,
            value: measure,
            label: intl.formatMessage(getCustomFilterMessage(reportCategory, appliedReportName, measure)),
        }));
    };

    const [measureOptions, setMeasureOptions] = useState(createMeasureOptions());
    const [selectedMeasure, setSelectedMeasure] = useState(measureOptions[0]);
    const [selectedOperator, setSelectedOperator] = useState(OPERATOR_OPTIONS[0]);
    const [customValue, setCustomValue] = useState('1');

    const isDuplicateFilter = customFilters.find(
        (filter) =>
            filter.measure === selectedMeasure.value &&
            filter.operator === selectedOperator.value &&
            filter.value === customValue,
    );

    useEffect(() => {
        const options = createMeasureOptions();
        setMeasureOptions(options);
        setSelectedMeasure(options.find((measure) => measure.key === selectedMeasure.key) || options[0]);
    }, [reportData, seasonOrGame, reportData, appliedReportName]);

    const handleMeasureUpdated = (e, { value }) => {
        setSelectedMeasure(measureOptions.find((measure) => measure.key === value));
    };

    const handleOperatorUpdated = (e, { value }) => {
        setSelectedOperator(OPERATOR_OPTIONS.find((option) => option.value === value));
    };

    const updateFilterValue = (e) => {
        // allow + or - decimal number
        const isTextAValidNumber = /^[-+]?\d*\.?\d*$/.test(e.target.value);
        const maxAllowedValue = 1000000000;

        if (!isTextAValidNumber || e.target.value > maxAllowedValue) {
            return;
        }
        setCustomValue(e.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const newCustomFilter = {
            measure: selectedMeasure.value,
            operator: selectedOperator.value,
            value: customValue,
        };

        dispatch(filterActionCreators.setCustomFilters([...customFilters, newCustomFilter]));
    };

    return (
        <InputsContainer onSubmit={handleSubmit}>
            <Select
                isSelectDisabled={!canAnotherFilterBeAdded}
                isSearchEnabled
                onOptionChange={handleMeasureUpdated}
                options={measureOptions}
                selectedOption={selectedMeasure}
                optionsBoxAriaLabel=""
                dropdownButtonAriaLabel=""
            />
            <Select
                isSelectDisabled={!canAnotherFilterBeAdded}
                isSearchEnabled={false}
                onOptionChange={handleOperatorUpdated}
                options={OPERATOR_OPTIONS}
                selectedOption={selectedOperator}
                optionsBoxAriaLabel=""
                dropdownButtonAriaLabel=""
            />
            <ValueInput disabled={!canAnotherFilterBeAdded} onChange={updateFilterValue} value={customValue} />
            {canAnotherFilterBeAdded && (
                <AddButton type="submit" variant={ButtonType.CTA} disabled={isDuplicateFilter}>
                    +
                </AddButton>
            )}
        </InputsContainer>
    );
};

export default Inputs;
